import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import {
  BASE_FEE_ICON,
  CheckMark,
  HEADING_RICH_TAGS,
  type IncludedCategory,
  InfinityIcon,
  T,
  includedCategories,
  includedIntro,
  supportFeeSub,
  supportFeeTitle,
  unlimitedRows,
} from "./pricing-shared";
import { PricingSupportFeeTiers } from "./pricing-support-fee-tiers";

const HeadIcon = BASE_FEE_ICON;

function CategoryHead({
  children,
  ...cat
}: { readonly children?: ReactNode } & Pick<
  IncludedCategory,
  "title" | "sub"
> & {
    readonly count?: string;
    readonly icon: ReactNode;
  }): ReactNode {
  return (
    <div style={{ display: "flex", gap: 15, alignItems: "flex-start" }}>
      <span
        style={{
          width: 42,
          height: 42,
          borderRadius: 9,
          background: "#EFEBE2",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
        }}
      >
        {cat.icon}
      </span>
      <div>
        <h3
          style={{
            fontFamily: T.display,
            fontWeight: 600,
            fontSize: 25,
            lineHeight: 1.1,
            color: T.ink,
            margin: 0,
          }}
        >
          {cat.title}
        </h3>
        <p
          style={{
            fontFamily: T.sans,
            fontSize: 14.5,
            lineHeight: 1.5,
            color: "#575B63",
            margin: "7px 0 0",
          }}
        >
          {cat.sub}
        </p>
        {cat.count ? (
          <span
            style={{
              display: "inline-block",
              marginTop: 12,
              fontFamily: T.label,
              fontSize: 10.5,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#9AA0A6",
            }}
          >
            {cat.count}
          </span>
        ) : null}
        {children}
      </div>
    </div>
  );
}

const ROW_GRID = {
  display: "grid",
  gridTemplateColumns: "330px 1fr",
  gap: 56,
} as const;

const CHECKLIST_GRID = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "0 40px",
  alignContent: "start",
} as const;

/** Desktop "Das ist alles dabei" catalog: unlimited highlight + categories + support. */
export function DesktopIncluded(): ReactNode {
  const tPricingShared = useTranslations("public.pricingShared");
  const t = useTranslations("public.pricingDesktopIncluded");
  return (
    <div
      id="enthalten"
      data-screen-label={t("enthalten")}
      style={{ maxWidth: 1180, margin: "0 auto", padding: "40px 28px 92px" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 32,
          flexWrap: "wrap",
          margin: "8px 0 44px",
        }}
      >
        <h2
          style={{
            fontFamily: T.display,
            fontWeight: 600,
            fontSize: 46,
            lineHeight: 1.04,
            letterSpacing: "-0.02em",
            color: T.ink,
            margin: 0,
            maxWidth: 560,
          }}
        >
          {t.rich("allesDabei", HEADING_RICH_TAGS)}
        </h2>
        <p
          style={{
            fontFamily: T.sans,
            fontSize: 16,
            lineHeight: 1.55,
            color: "#575B63",
            margin: "0 0 6px auto",
            maxWidth: 360,
          }}
        >
          {includedIntro(tPricingShared)}
        </p>
      </div>

      <div style={{ position: "relative", marginBottom: 10 }}>
        <div
          style={{
            position: "absolute",
            inset: "0 -22px",
            background: "#FAFAFA",
            border: "1px solid rgba(11,15,25,0.10)",
            borderRadius: 14,
          }}
        />
        <div
          style={{
            ...ROW_GRID,
            position: "relative",
            alignItems: "start",
            padding: "24px 0",
          }}
        >
          <CategoryHead
            icon={<InfinityIcon size={21} />}
            title={t("keinAufpreisProKopf")}
            sub={t("egalVieleMitarbeiterZugaenge")}
            count={t("unbegrenzt")}
          />
          <div style={CHECKLIST_GRID}>
            {unlimitedRows(tPricingShared).map((row) => (
              <div key={row.label} style={{ display: "contents" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "11px 0",
                    borderTop: "1px solid rgba(11,15,25,0.06)",
                  }}
                >
                  <InfinityIcon size={15} strokeWidth={2.2} />
                  <span
                    style={{
                      fontFamily: T.sans,
                      fontSize: 15,
                      fontWeight: 500,
                      color: "#3F444D",
                    }}
                  >
                    {row.label}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "11px 0 11px 27px",
                    borderTop: "1px solid rgba(11,15,25,0.06)",
                  }}
                >
                  <span
                    style={{
                      fontFamily: T.sans,
                      fontSize: 13.5,
                      color: "#8A8D94",
                    }}
                  >
                    {row.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {includedCategories(tPricingShared).map((cat, i) => {
        const Icon = cat.icon;
        return (
          <div
            key={cat.title}
            style={{
              ...ROW_GRID,
              padding: "40px 0",
              borderTop: i === 0 ? "0" : "1px solid rgba(11,15,25,0.10)",
            }}
          >
            <CategoryHead
              icon={
                <Icon
                  size={19}
                  strokeWidth={1.7}
                  color="#3F444D"
                  aria-hidden="true"
                />
              }
              title={cat.title}
              sub={cat.sub}
              count={cat.count}
            />
            <div style={CHECKLIST_GRID}>
              {cat.items.map((item) => (
                <div
                  key={item.name}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "11px 0",
                    borderTop: "1px solid rgba(11,15,25,0.06)",
                  }}
                >
                  {/* Box = one text line, so the check sits on line 1 when the name wraps. */}
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: 21,
                      flex: "none",
                    }}
                  >
                    <CheckMark />
                  </span>
                  <span
                    style={{
                      flex: 1,
                      fontFamily: T.sans,
                      fontSize: 15,
                      lineHeight: "21px",
                      fontWeight: 500,
                      color: "#3F444D",
                    }}
                  >
                    {item.name}
                  </span>
                  {item.bald ? (
                    <span
                      style={{
                        alignSelf: "center",
                        fontFamily: T.label,
                        fontSize: 9,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "#8A8D94",
                        border: "1px solid rgba(11,15,25,0.16)",
                        borderRadius: 999,
                        padding: "2px 7px",
                      }}
                    >
                      {t("bald")}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div
        style={{
          ...ROW_GRID,
          padding: "40px 0",
          borderTop: "1px solid rgba(11,15,25,0.10)",
        }}
      >
        <CategoryHead
          icon={
            <HeadIcon
              size={19}
              strokeWidth={1.7}
              color="#3F444D"
              aria-hidden="true"
            />
          }
          title={supportFeeTitle(tPricingShared)}
          sub={supportFeeSub(tPricingShared)}
        />
        <PricingSupportFeeTiers compact={false} />
      </div>
    </div>
  );
}
